'use client'

import React, { useState, useEffect, useRef } from 'react'
import { useSession, signIn, signOut } from 'next-auth/react'
import { Elements } from '@stripe/react-stripe-js'
import { Header } from '@/app/components/common/Header'
import { Button } from '@/app/components/common/Button'
import { Input } from '@/app/components/common/Input'
import { PhoneInput, isValidPhoneNumber } from '@/app/components/common/PhoneInput'
import { PaymentForm } from '@/app/components/booking/PaymentForm'
import { getStripe } from '@/lib/stripe'
import { ContractSigning } from '@/app/components/contracts/ContractSigning'
import { useBookingLayout } from '@/app/components/booking/BookingLayout'
import { logError, logWarn, debug } from '@/lib/logging'
import toast from 'react-hot-toast'

const CC_SURCHARGE_RATE = 0.03 // 3% credit card surcharge

interface Package {
  id: string
  name: string
  description?: string
  payInFullPrice: number
  splitPaymentPrice: number
  durationMinutes?: number
  hours?: number
  visits?: number
  contractTemplateId?: string
}

interface PackageCheckoutProps {
  package: Package
  providerId: string
  providerName: string
  orgId: string
  preVerifiedEmail?: string
  onPurchaseComplete: (paymentId: string) => void
  onBack: () => void
}

type CheckoutStep = 'auth' | 'verify-code' | 'contact' | 'contract' | 'payment'

export function PackageCheckout({
  package: pkg,
  providerId,
  providerName,
  orgId,
  preVerifiedEmail,
  onPurchaseComplete,
  onBack,
}: PackageCheckoutProps) {
  const { data: session, status } = useSession()
  const { collapsed, collapse } = useBookingLayout()
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('auth')
  const [paymentType, setPaymentType] = useState<'full' | 'split'>('full')
  const [paymentMethodType, setPaymentMethodType] = useState<'card' | 'ach'>('card')
  const [protectionType, setProtectionType] = useState<'none' | 'transfer' | 'refund'>('none')
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    dueDate: '',
  })
  const [authEmail, setAuthEmail] = useState('')
  const [authCode, setAuthCode] = useState('')
  const [contractId, setContractId] = useState<string | null>(null)
  const [useDocuSign, setUseDocuSign] = useState(false)
  const [signingUrl, setSigningUrl] = useState<string | undefined>()
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  // Track whether initial step determination has run, so session refetches
  // don't reset the user back to 'contact' after they've advanced.
  const hasInitializedStep = useRef(false)

  // Determine initial step based on auth status (runs once on mount/login)
  useEffect(() => {
    if (status === 'loading') return

    // After initial determination, only re-run if the user signs out
    if (hasInitializedStep.current) {
      if (!session?.user) {
        // Session was lost (e.g., explicit sign-out via handleSwitchAccount)
        hasInitializedStep.current = false
        setCurrentStep('auth')
      }
      return
    }

    if (session?.user) {
      hasInitializedStep.current = true
      debug('ui', 'User already authenticated, proceeding to contact info')
      // Fetch full user profile to pre-fill contact info including address
      const fetchUserProfile = async () => {
        try {
          const response = await fetch('/api/user/profile')
          if (response.ok) {
            const profile = await response.json()
            setContactInfo({
              name: profile.name || '',
              email: profile.email || '',
              phone: profile.phone || '',
              address1: profile.address1 || '',
              address2: profile.address2 || '',
              city: profile.city || '',
              state: profile.state || '',
              zip: profile.zipCode || '',
              dueDate: profile.dueDate ? profile.dueDate.split('T')[0] : '',
            })
          } else {
            // Fallback to session data
            setContactInfo({
              name: session.user.name || '',
              email: session.user.email || '',
              phone: '',
              address1: '',
              address2: '',
              city: '',
              state: '',
              zip: '',
              dueDate: '',
            })
          }
        } catch (error) {
          logError('Failed to fetch user profile:', error)
          // Fallback to session data
          setContactInfo({
            name: session.user.name || '',
            email: session.user.email || '',
            phone: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            dueDate: '',
          })
        }
      }
      fetchUserProfile()
      setCurrentStep('contact')
    } else if (preVerifiedEmail && !authEmail) {
      hasInitializedStep.current = true
      // Email was pre-verified (e.g., from custom package flow), send code automatically
      debug('ui', 'Pre-verified email provided, sending auth code automatically')
      setAuthEmail(preVerifiedEmail)
      const sendCode = async () => {
        try {
          setLoading(true)
          const response = await fetch('/api/auth/send-code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: preVerifiedEmail }),
          })
          const result = await response.json()
          if (!response.ok) {
            throw new Error(result.error || 'Failed to send code')
          }
          setCurrentStep('verify-code')
        } catch (error) {
          logError('Auto send code error:', error)
          setErrors({ submit: error instanceof Error ? error.message : 'Failed to send code' })
          setCurrentStep('auth')
        } finally {
          setLoading(false)
        }
      }
      sendCode()
    } else {
      setCurrentStep('auth')
    }
  }, [session, status, preVerifiedEmail])

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true)
      // Build callback URL with package ID to restore state after OAuth
      const currentUrl = new URL(window.location.href)
      const separator = currentUrl.search ? '&' : '?'
      const callbackUrl = `${window.location.href}${separator}packageId=${pkg.id}`

      await signIn('google', {
        callbackUrl,
        redirect: false,
      })
    } catch (error) {
      logError('Google sign-in error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleYahooSignIn = async () => {
    try {
      setLoading(true)
      const currentUrl = new URL(window.location.href)
      const separator = currentUrl.search ? '&' : '?'
      const callbackUrl = `${window.location.href}${separator}packageId=${pkg.id}`

      await signIn('yahoo', {
        callbackUrl,
        redirect: false,
      })
    } catch (error) {
      logError('Yahoo sign-in error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSendCode = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    setErrors({})

    if (!authEmail.trim()) {
      setErrors({ email: 'Email is required' })
      return
    }

    if (!/\S+@\S+\.\S+/.test(authEmail)) {
      setErrors({ email: 'Please enter a valid email address' })
      return
    }

    try {
      setLoading(true)

      const response = await fetch('/api/auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authEmail }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send code')
      }

      setCurrentStep('verify-code')
    } catch (error) {
      logError('Send code error:', error)
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to send code' })
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async (
    e?: React.SyntheticEvent<HTMLFormElement>,
    codeValue?: string
  ) => {
    if (e) e.preventDefault()

    // Prevent double submission
    if (loading) return

    setErrors({})

    // Use provided code value or fall back to state
    const code = codeValue || authCode
    if (!code.trim() || code.length !== 6) {
      setErrors({ code: 'Please enter the 6-digit code' })
      return
    }

    try {
      setLoading(true)

      // Sign in with code via NextAuth
      const result = await signIn('credentials', {
        email: authEmail,
        code: code,
        redirect: false,
      })

      if (result?.error) {
        throw new Error('Invalid or expired code')
      }

      // Session is automatically established after successful sign-in
      // Clear auth state to allow useEffect to progress to contact info
      setAuthEmail('')
      setAuthCode('')
    } catch (error) {
      logError('Code verification error:', error)
      setErrors({ submit: error instanceof Error ? error.message : 'Verification failed' })
    } finally {
      setLoading(false)
    }
  }

  const validateContactInfo = () => {
    const newErrors: Record<string, string> = {}

    if (!contactInfo.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!contactInfo.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(contactInfo.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!contactInfo.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    } else if (!isValidPhoneNumber(contactInfo.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number'
    }

    if (!contactInfo.address1.trim()) {
      newErrors.address1 = 'Street address is required for home visits'
    }

    if (!contactInfo.city.trim()) {
      newErrors.city = 'City is required'
    }

    if (!contactInfo.state.trim()) {
      newErrors.state = 'State is required'
    }

    if (!contactInfo.zip.trim()) {
      newErrors.zip = 'ZIP code is required'
    } else if (!/^\d{5}(-\d{4})?$/.test(contactInfo.zip)) {
      newErrors.zip = 'Please enter a valid ZIP code'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleContactSubmit = async () => {
    if (!validateContactInfo()) return

    try {
      setLoading(true)

      await fetch('/api/user/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contactInfo.name,
          phone: contactInfo.phone,
          address1: contactInfo.address1,
          address2: contactInfo.address2,
          city: contactInfo.city,
          state: contactInfo.state,
          zipCode: contactInfo.zip,
        }),
      })
    } catch (err) {
      logWarn('Failed to save contact info to profile, continuing anyway', err)
    } finally {
      setLoading(false)
    }

    if (pkg.contractTemplateId) {
      try {
        setLoading(true)
        const response = await fetch('/api/contracts/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            packageId: pkg.id,
            providerProfileId: providerId,
          }),
        })

        if (!response.ok) {
          const errorBody = (await response.json()) as { error?: string }
          const errorMsg = errorBody.error || 'Failed to create contract'
          // If the package no longer has a contract template, skip to payment
          if (errorMsg.includes('does not have a contract template')) {
            debug('ui', 'Package has no contract template, skipping to payment')
            setCurrentStep('payment')
            return
          }
          throw new Error(errorMsg)
        }

        const data = (await response.json()) as {
          contractId: string
          status: string
          useDocuSign?: boolean
          signingUrl?: string
        }
        setContractId(data.contractId)
        setUseDocuSign(!!data.useDocuSign)
        setSigningUrl(data.signingUrl)

        if (data.status === 'unsigned') {
          collapse()
          setCurrentStep('contract')
        } else if (data.status === 'signed_unpaid') {
          // Contract already signed (e.g., user was bounced back after signing)
          // Skip directly to payment
          collapse()
          setCurrentStep('payment')
        } else {
          // Already fully paid (signed_paid), skip to payment confirmation
          collapse()
          setCurrentStep('payment')
        }
      } catch (error) {
        logError('Failed to create contract:', error)
        toast.error('Failed to prepare contract. Please try again.')
      } finally {
        setLoading(false)
      }
    } else {
      setCurrentStep('payment')
    }
  }

  const handlePaymentSuccess = (paymentId: string, isProcessing?: boolean) => {
    if (isProcessing) {
      debug('ui', `ACH payment submitted for processing: ${paymentId}`)
    } else {
      debug('ui', `Purchase completed successfully: ${paymentId}`)
    }
    // Pass paymentId to parent - parent can check payment status to determine if ACH is still processing
    onPurchaseComplete(paymentId)
  }

  const handlePaymentError = (error: string) => {
    logError('Payment error:', error)
    // Error display is handled in PaymentForm
  }

  const handleSwitchAccount = async () => {
    try {
      setLoading(true)
      // Clear contact info
      setContactInfo({
        name: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        dueDate: '',
      })
      // Sign out and return to auth step
      hasInitializedStep.current = false
      await signOut({ redirect: false })
      setCurrentStep('auth')
    } catch (error) {
      logError('Sign out error:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price)
  }

  const getBaseAmount = () => {
    return paymentType === 'split' ? pkg.splitPaymentPrice : pkg.payInFullPrice
  }

  const getSurcharge = () => {
    if (paymentMethodType !== 'card') return 0
    return Math.round((getBaseAmount() + getProtectionFee()) * CC_SURCHARGE_RATE * 100) / 100 // Round to cents
  }

  const getProtectionFee = () => {
    if (protectionType === 'none') return 0
    const rate = protectionType === 'transfer' ? 0.03 : 0.06
    return Math.round(pkg.payInFullPrice * rate * 100) / 100
  }

  const handleContractSigned = () => {
    setCurrentStep('payment')
  }

  const renderContractStep = () => {
    if (!contractId) return null
    return (
      <ContractSigning
        contractId={contractId}
        onComplete={handleContractSigned}
        onBack={() => setCurrentStep('contact')}
        useDocuSign={useDocuSign}
        signingUrl={signingUrl}
      />
    )
  }

  const renderAuthStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Header className="mb-2">Continue to Purchase</Header>
        <p className="text-gray-600 text-sm">Enter your email to continue with {pkg.name}</p>
      </div>

      {/* Email Entry Form */}
      <form onSubmit={handleSendCode} className="space-y-4">
        {errors.submit && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{errors.submit}</div>
        )}

        <Input
          label="Email Address"
          type="email"
          value={authEmail}
          onChange={(e) => setAuthEmail(e.target.value)}
          error={errors.email}
          required
          fullWidth
          placeholder="you@example.com"
          autoComplete="email"
          autoFocus
        />

        <Button type="submit" fullWidth disabled={loading}>
          {loading ? 'Sending code...' : 'Continue'}
        </Button>
      </form>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">Or continue with</span>
        </div>
      </div>

      {/* OAuth Sign In */}
      <div className="flex gap-3">
        <Button
          fullWidth
          variant="outline"
          onClick={handleGoogleSignIn}
          disabled={loading}
          aria-label="Sign in with Google"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        </Button>
        <Button
          fullWidth
          variant="outline"
          onClick={handleYahooSignIn}
          disabled={loading}
          aria-label="Sign in with Yahoo"
        >
          <svg className="w-5 h-5" viewBox="0 0 512 512">
            <path
              fill="#5F01D1"
              d="M223.69 141.06L167 284.23l-56-143.17H14.93l105.83 249.13L82.19 480h94.17l140.91-338.94Zm105.4 135.79a58.22 58.22 0 1 0 58.22 58.22a58.22 58.22 0 0 0-58.22-58.22M394.65 32l-93 223.47h104.79L499.07 32Z"
            />
          </svg>
        </Button>
      </div>

      {/* Terms & Privacy */}
      <div className="text-center text-xs text-gray-500">
        By continuing, you agree to our{' '}
        <a href="/terms" target="_blank" className="text-primary hover:underline">
          Terms & Conditions
        </a>{' '}
        and{' '}
        <a href="/privacy" target="_blank" className="text-primary hover:underline">
          Privacy Policy
        </a>
      </div>

      {/* Back to Packages */}
      <div className="text-center">
        <Button variant="outline" onClick={onBack}>
          ← Back to Packages
        </Button>
      </div>
    </div>
  )

  const renderVerifyCodeStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Header className="mb-2">Enter your code</Header>
        <p className="text-gray-600 text-sm">
          We sent a 6-digit code to <strong>{authEmail}</strong>
        </p>
        <p className="text-danger text-sm font-medium mt-2">
          Don&apos;t see it? Check your spam or junk folder.
        </p>
        <button
          onClick={() => setCurrentStep('auth')}
          className="text-primary text-sm hover:underline mt-2"
        >
          Change email
        </button>
      </div>

      <form onSubmit={handleVerifyCode} className="space-y-4">
        {errors.submit && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{errors.submit}</div>
        )}

        <Input
          label="Verification Code"
          type="text"
          value={authCode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6)
            setAuthCode(value)
            // Clear any existing errors
            if (errors.code || errors.submit) {
              setErrors({})
            }
            // Auto-submit when 6 digits are entered
            if (value.length === 6 && !loading) {
              // Pass the value directly instead of relying on state
              handleVerifyCode(undefined, value)
            }
          }}
          error={errors.code}
          required
          fullWidth
          placeholder="000000"
          autoComplete="off"
          autoFocus
          maxLength={6}
          className="text-center"
        />

        <Button type="submit" fullWidth disabled={loading || authCode.length !== 6}>
          {loading ? 'Verifying...' : 'Verify Code'}
        </Button>

        <div className="text-center text-sm text-gray-600">
          <p>We sent you an email with a verification code. Didn&apos;t receive the code?</p>
          <button
            type="button"
            onClick={() => setCurrentStep('auth')}
            className="text-primary hover:underline"
          >
            Resend code
          </button>
        </div>
      </form>
    </div>
  )

  const renderContactStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Header className="mb-2">Contact Information</Header>
        <p className="text-gray-600 text-sm">
          We&apos;ll use this information for scheduling and home visits
        </p>
        {session?.user && (
          <button
            type="button"
            onClick={handleSwitchAccount}
            disabled={loading}
            className="text-primary text-base hover:underline mt-3 font-semibold"
          >
            Switch account
          </button>
        )}
      </div>

      <div className="space-y-4">
        <Input
          label="Full Name"
          value={contactInfo.name}
          onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
          error={errors.name}
          required
          fullWidth
          placeholder="Jane Smith"
        />

        <Input
          label="Email Address"
          type="email"
          value={contactInfo.email}
          onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
          error={errors.email}
          required
          fullWidth
          readOnly
          placeholder="jane@example.com"
        />

        <PhoneInput
          label="Phone Number"
          value={contactInfo.phone}
          onChange={(value) => setContactInfo({ ...contactInfo, phone: value })}
          error={errors.phone}
          required
          fullWidth
          placeholder="(555) 123-4567"
        />

        {/* Address Fields */}
        <Input
          label="Street Address"
          value={contactInfo.address1}
          onChange={(e) => setContactInfo({ ...contactInfo, address1: e.target.value })}
          error={errors.address1}
          required
          fullWidth
          placeholder="123 Main Street"
        />

        <Input
          label="Apartment, Suite, etc. (Optional)"
          value={contactInfo.address2}
          onChange={(e) => setContactInfo({ ...contactInfo, address2: e.target.value })}
          error={errors.address2}
          fullWidth
          placeholder="Apt 4B"
        />

        {/* City, State, ZIP in a grid layout */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="City"
            value={contactInfo.city}
            onChange={(e) => setContactInfo({ ...contactInfo, city: e.target.value })}
            error={errors.city}
            required
            fullWidth
            placeholder="San Francisco"
          />
          <Input
            label="State"
            value={contactInfo.state}
            onChange={(e) => setContactInfo({ ...contactInfo, state: e.target.value })}
            error={errors.state}
            required
            fullWidth
            placeholder="CA"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="ZIP Code"
            value={contactInfo.zip}
            onChange={(e) => setContactInfo({ ...contactInfo, zip: e.target.value })}
            error={errors.zip}
            required
            fullWidth
            placeholder="94102"
          />
          <div></div> {/* Empty space to balance the grid */}
        </div>

        {/* Due Date / Birth Date */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <Input
            label="Expected Due Date or Baby's Birth Date"
            type="date"
            value={contactInfo.dueDate}
            onChange={(e) => setContactInfo({ ...contactInfo, dueDate: e.target.value })}
            fullWidth
          />
          <p className="text-sm text-gray-500 mt-2">
            This helps your provider plan your care. You can skip this if you prefer.
          </p>
        </div>
      </div>

      <div className="flex space-x-3">
        <Button variant="outline" onClick={() => setCurrentStep('auth')} fullWidth>
          ← Back
        </Button>
        <Button onClick={handleContactSubmit} fullWidth disabled={loading}>
          {loading
            ? 'Please wait...'
            : pkg.contractTemplateId
              ? 'Continue to Contract'
              : 'Continue to Payment'}
        </Button>
      </div>
    </div>
  )

  const renderPaymentStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Header className="mb-2">Complete Your Purchase</Header>
        <p className="text-gray-600 text-sm">
          {pkg.name} with {providerName}
        </p>
      </div>

      {/* Payment Method Selection */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">Payment Method</label>
        <div className="space-y-2">
          <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="paymentMethodType"
              value="card"
              checked={paymentMethodType === 'card'}
              onChange={(e) => setPaymentMethodType(e.target.value as 'card' | 'ach')}
              className="text-primary"
            />
            <div className="flex-1">
              <div className="font-medium">Credit / Debit Card</div>
              <div className="text-sm text-gray-600">Instant payment • 3% processing fee</div>
            </div>
          </label>
          <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="paymentMethodType"
              value="ach"
              checked={paymentMethodType === 'ach'}
              onChange={(e) => setPaymentMethodType(e.target.value as 'card' | 'ach')}
              className="text-primary"
            />
            <div className="flex-1">
              <div className="font-medium">
                Bank Account (ACH) <span className="text-primary font-semibold">— Save 3%</span>
              </div>
              <div className="text-sm text-gray-600">
                No processing fee • Takes 2-5 business days
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Payment Type Selection */}
      {pkg.splitPaymentPrice < pkg.payInFullPrice && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">Payment Option</label>
          <div className="space-y-2">
            <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="paymentType"
                value="full"
                checked={paymentType === 'full'}
                onChange={(e) => setPaymentType(e.target.value as 'full' | 'split')}
                className="text-primary"
              />
              <div className="flex-1">
                <div className="font-medium">Pay in Full</div>
                <div className="text-sm text-gray-600">{formatPrice(pkg.payInFullPrice)}</div>
              </div>
            </label>
            <label className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="paymentType"
                value="split"
                checked={paymentType === 'split'}
                onChange={(e) => setPaymentType(e.target.value as 'full' | 'split')}
                className="text-primary"
              />
              <div className="flex-1">
                <div className="font-medium">Split Payment</div>
                <div className="text-sm text-gray-600">
                  {formatPrice(pkg.splitPaymentPrice)} now, pay remaining later
                </div>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Care Protection */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">Care Protection</label>
          <p className="text-xs text-gray-500 mt-0.5">
            Protect your care plan if your needs change. Designed to balance flexibility for
            families with income stability for providers.
          </p>
        </div>
        <div className="space-y-2">
          <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="protectionType"
              value="none"
              checked={protectionType === 'none'}
              onChange={() => setProtectionType('none')}
              className="text-primary mt-1"
            />
            <div className="flex-1">
              <div className="font-medium">No Protection</div>
              <div className="text-sm text-gray-600">Standard terms from your contract apply</div>
            </div>
          </label>
          <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 border-primary/30 bg-primary/5">
            <input
              type="radio"
              name="protectionType"
              value="transfer"
              checked={protectionType === 'transfer'}
              onChange={() => setProtectionType('transfer')}
              className="text-primary mt-1"
            />
            <div className="flex-1">
              <div className="font-medium">
                Transfer Protection (3%){' '}
                <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                  Recommended
                </span>
              </div>
              <div className="text-sm text-gray-600">
                Switch to another provider if your needs change
              </div>
              <div className="text-sm font-medium text-gray-700 mt-1">
                +{formatPrice(Math.round(pkg.payInFullPrice * 0.03 * 100) / 100)}
              </div>
              <details className="mt-1">
                <summary className="text-xs text-primary cursor-pointer">Details</summary>
                <div className="text-xs text-gray-600 mt-1 space-y-1">
                  <p>Transfer unused care to another provider on the platform</p>
                  <p>Receive either:</p>
                  <ul className="list-disc list-inside pl-2 space-y-0.5">
                    <li>100% credit for services canceled 30+ days in advance, or</li>
                    <li>70% credit for services canceled more than 7 days in advance</li>
                  </ul>
                  <p>We automatically apply whichever option gives you the greater value</p>
                  <ul className="list-disc list-inside pl-2 space-y-0.5">
                    <li>Visits within 7 days are non-transferable</li>
                    <li>Credits must be used within 12 months</li>
                    <li>Protection fee is non-refundable</li>
                  </ul>
                </div>
              </details>
            </div>
          </label>
          <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="protectionType"
              value="refund"
              checked={protectionType === 'refund'}
              onChange={() => setProtectionType('refund')}
              className="text-primary mt-1"
            />
            <div className="flex-1">
              <div className="font-medium">Refund Protection (6%)</div>
              <div className="text-sm text-gray-600">Receive a refund if your plans change</div>
              <div className="text-sm font-medium text-gray-700 mt-1">
                +{formatPrice(Math.round(pkg.payInFullPrice * 0.06 * 100) / 100)}
              </div>
              <details className="mt-1">
                <summary className="text-xs text-primary cursor-pointer">Details</summary>
                <div className="text-xs text-gray-600 mt-1 space-y-1">
                  <p>Receive a refund for unused services</p>
                  <p>Receive either:</p>
                  <ul className="list-disc list-inside pl-2 space-y-0.5">
                    <li>100% refunded for services canceled 30+ days in advance, or</li>
                    <li>70% refunded for services canceled more than 7 days in advance</li>
                  </ul>
                  <p>We automatically apply whichever option gives you the greater refund</p>
                  <ul className="list-disc list-inside pl-2 space-y-0.5">
                    <li>Visits within 7 days are non-refundable</li>
                    <li>Applies only to services not yet used</li>
                    <li>Protection fee is non-refundable</li>
                  </ul>
                </div>
              </details>
            </div>
          </label>
        </div>
      </div>

      <Elements stripe={getStripe()}>
        <PaymentForm
          baseAmount={getBaseAmount()}
          surcharge={getSurcharge()}
          protectionFee={getProtectionFee()}
          protectionType={protectionType}
          packageId={pkg.id}
          providerId={providerId}
          orgId={orgId}
          paymentType={paymentType}
          paymentMethod={paymentMethodType}
          contactInfo={contactInfo}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      </Elements>

      <div className="text-center">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(contractId ? 'contract' : 'contact')}
        >
          ← Back
        </Button>
      </div>
    </div>
  )

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'auth':
        return renderAuthStep()
      case 'verify-code':
        return renderVerifyCodeStep()
      case 'contact':
        return renderContactStep()
      case 'contract':
        return renderContractStep()
      case 'payment':
        return renderPaymentStep()
      default:
        return null
    }
  }

  return (
    <div data-component-name="PackageCheckout" className={collapsed ? '' : 'max-w-md mx-auto'}>
      {/* Package Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <Header className="mb-2">{pkg.name}</Header>
        {pkg.description && <p className="text-sm text-gray-600 mb-2">{pkg.description}</p>}
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Package Price:</span>
          <span className="font-bold text-lg">{formatPrice(pkg.payInFullPrice)}</span>
        </div>
        {pkg.splitPaymentPrice < pkg.payInFullPrice && (
          <div className="flex justify-between items-center mt-1">
            <span className="text-sm text-gray-600">Minimum Deposit:</span>
            <span className="text-sm font-medium">{formatPrice(pkg.splitPaymentPrice)}</span>
          </div>
        )}
      </div>

      {/* Checkout Steps */}
      {renderCurrentStep()}
    </div>
  )
}
